import { LockClosedIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '@main/complykit/components/button';
import { Card } from '@main/complykit/components/card';
import { Checkbox } from '@main/complykit/components/checkbox';
import { Combobox } from '@main/complykit/components/combobox';
import { Fieldset } from '@main/complykit/components/fieldset';
import { IconButton } from '@main/complykit/components/icon-button';
import { Option } from '@main/complykit/components/list-box';
import { Select } from '@main/complykit/components/select';
import { Content } from '@main/complykit/components/slots';
import { Stack } from '@main/complykit/components/stack';
import { TextArea } from '@main/complykit/components/text-area';
import { TextField } from '@main/complykit/components/text-field';
import { TimePeriod } from '@main/ui';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ArrayPath,
  Controller,
  ControllerRenderProps,
  FieldArray,
  FieldError,
  FieldPath,
  FieldValues,
  get,
  useController,
  useFieldArray,
  UseFormReturn,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { MarkdownInline } from '../markdown-inline';
import {
  ArrayFormFieldNode,
  FieldSlot,
  FieldSlotDescriptor,
  FieldSlotRegistry,
  FieldSlotRenderer,
  FormArrayElement,
  FormFieldNode,
  SchemaFormProps,
  TextFormFieldNode,
  UnionFormFieldNode,
} from './model';

/* Labels and typed values are the person's own text: names read aloud must not carry HTML entities. */
const asText = { interpolation: { escapeValue: false } } as const;

type SchemaFormContextValue<
  TValues extends FieldValues,
  TMeta,
  TSlot extends FieldSlotDescriptor,
> = {
  form: UseFormReturn<TValues>;
  isDisabled: boolean;
  slotRegistry: FieldSlotRegistry<TValues, TMeta, TSlot>;
};

const SchemaFormContext = createContext<SchemaFormContextValue<
  FieldValues,
  unknown,
  FieldSlotDescriptor
> | null>(null);

const EMPTY_SLOT_REGISTRY = {};

export function SchemaForm<
  TValues extends FieldValues,
  TMeta = never,
  TResult = TValues,
  TSlot extends FieldSlotDescriptor = never,
>(props: SchemaFormProps<TValues, TMeta, TResult, TSlot>) {
  const { model, form, isDisabled = false } = props;
  const slotRegistry = (props.slotRegistry ?? EMPTY_SLOT_REGISTRY) as FieldSlotRegistry<
    TValues,
    TMeta,
    TSlot
  >;
  const contextValue = useMemo(
    () => toSchemaFormContextValue({ form, isDisabled, slotRegistry }),
    [form, isDisabled, slotRegistry],
  );

  return (
    <SchemaFormContext.Provider value={contextValue}>
      <FieldStack fields={model.fields} />
    </SchemaFormContext.Provider>
  );
}

/** What every field's control takes, resolved once from the node and the form. */
type FieldAnatomy = {
  label: string;
  description?: ReactNode;
  error?: string;
  isRequired: boolean;
  isDisabled: boolean;
  isReadOnly: boolean;
};

function SchemaField<TValues extends FieldValues, TMeta, TSlot extends FieldSlotDescriptor>({
  node,
}: {
  node: FormFieldNode<TValues, TMeta, TSlot>;
}) {
  const context = useSchemaFormContext<TValues, TMeta, TSlot>();
  const error = findError(context.form.formState.errors, node.name);
  const footer = renderSlot(node.slots?.footer, node, context);
  const anatomy: FieldAnatomy = {
    label: node.label,
    description: node.description ? <MarkdownInline text={node.description} /> : undefined,
    error: error?.message || undefined,
    isRequired: node.required.input,
    isDisabled: context.isDisabled || Boolean(node.input?.disabled),
    isReadOnly: Boolean(node.input?.readOnly),
  };

  if (!footer) {
    return <FieldInput node={node} anatomy={anatomy} />;
  }

  return (
    <Stack gap={2}>
      <FieldInput node={node} anatomy={anatomy} />
      <Stack direction="row" gap={2}>
        {footer}
      </Stack>
    </Stack>
  );
}

function FieldInput<TValues extends FieldValues, TMeta, TSlot extends FieldSlotDescriptor>({
  node,
  anatomy,
}: {
  node: FormFieldNode<TValues, TMeta, TSlot>;
  anatomy: FieldAnatomy;
}) {
  switch (node.kind) {
    case 'text':
      return <TextInput node={node} anatomy={anatomy} />;
    case 'number':
      return (
        <EntryInput
          node={node}
          anatomy={anatomy}
          normalize={normalizeNumber}
          format={formatScalar}
          inputMode="decimal"
        />
      );
    case 'date':
      return (
        <EntryInput
          node={node}
          anatomy={anatomy}
          normalize={normalizeDate}
          format={formatDate}
          type="date"
        />
      );
    case 'boolean':
      return <BooleanField node={node} anatomy={anatomy} />;
    case 'enum':
      return <EnumField node={node} anatomy={anatomy} />;
    case 'object':
      return <ObjectField node={node} anatomy={anatomy} />;
    case 'array':
      return isPrimitiveArrayElement(node.element) ? (
        <PrimitiveArrayField node={node} anatomy={anatomy} />
      ) : (
        <ObjectArrayField node={node} anatomy={anatomy} />
      );
    case 'union':
      return <UnionField node={node} anatomy={anatomy} />;
    case 'custom':
      return <CustomField node={node} anatomy={anatomy} />;
  }
}

/**
 * The text an entry shows, kept apart from the value it stores: the form
 * holds the normalized value (trimmed, a number, a date), and the entry keeps
 * what was typed until the value moves from outside.
 */
function useEntryText(
  field: ControllerRenderProps<FieldValues, string>,
  normalize: (text: string) => unknown,
  format: (value: unknown) => string,
  stored: () => unknown,
) {
  const [text, setText] = useState(() => format(field.value));

  useEffect(() => {
    // field.value falls back to the mount value once the store holds nothing, so
    // what the form actually stores decides whether the typed text still matches.
    const value = stored();
    setText((current) => (format(normalize(current)) === format(value) ? current : format(value)));
  }, [field.value, format, normalize, stored]);

  return [
    text,
    (next: string) => {
      setText(next);
      field.onChange(normalize(next));
    },
    () => {
      // Text that stores nothing (spaces only) is cleared on leaving, so it cannot
      // linger past a reset that never moves the value.
      if (normalize(text) === undefined) setText('');
      field.onBlur();
    },
  ] as const;
}

function EntryInput<TValues extends FieldValues, TMeta, TSlot extends FieldSlotDescriptor>({
  node,
  anatomy,
  normalize,
  format,
  type,
  inputMode,
}: {
  node: FormFieldNode<TValues, TMeta, TSlot>;
  anatomy: FieldAnatomy;
  normalize: (text: string) => unknown;
  format: (value: unknown) => string;
  type?: 'date';
  inputMode?: 'decimal';
}) {
  const { form } = useSchemaFormContext<TValues, TMeta, TSlot>();
  const { field } = useController({ name: node.name, control: form.control });
  const stored = useCallback((): unknown => form.getValues(node.name), [form, node.name]);
  const [text, setText, onBlur] = useEntryText(
    field as unknown as ControllerRenderProps<FieldValues, string>,
    normalize,
    format,
    stored,
  );

  return (
    <TextField
      {...anatomy}
      ref={field.ref}
      name={field.name}
      value={text}
      onChange={setText}
      onBlur={onBlur}
      type={type}
      inputMode={inputMode}
      placeholder={node.input?.placeholder}
    />
  );
}

function TextInput<TValues extends FieldValues, TMeta, TSlot extends FieldSlotDescriptor>({
  node,
  anatomy,
}: {
  node: TextFormFieldNode<TValues, TMeta, TSlot>;
  anatomy: FieldAnatomy;
}) {
  const { form } = useSchemaFormContext<TValues, TMeta, TSlot>();
  const { field } = useController({ name: node.name, control: form.control });
  const stored = useCallback((): unknown => form.getValues(node.name), [form, node.name]);
  const [text, setText, onBlur] = useEntryText(
    field as unknown as ControllerRenderProps<FieldValues, string>,
    normalizeText,
    formatScalar,
    stored,
  );
  const shared = {
    ...anatomy,
    ref: field.ref,
    name: field.name,
    value: text,
    onChange: setText,
    onBlur,
    placeholder: node.input?.placeholder,
  };

  if (node.input?.control === 'textarea') {
    return <TextArea {...shared} minRows={5} maxRows={12} />;
  }

  if (node.input?.isSecret) {
    // Write-only: masked with no reveal, like every password entry in the product. A browser
    // ignores autoComplete="off" on a password entry; new-password keeps it from filling the
    // person's own sign-in password into a connector secret.
    return (
      <TextField
        {...shared}
        type="password"
        autoComplete="new-password"
        iconStart={LockClosedIcon}
      />
    );
  }

  return <TextField {...shared} />;
}

function BooleanField<TValues extends FieldValues, TMeta, TSlot extends FieldSlotDescriptor>({
  node,
  anatomy: { label, isRequired: _isRequired, ...anatomy },
}: {
  node: FormFieldNode<TValues, TMeta, TSlot>;
  anatomy: FieldAnatomy;
}) {
  const { form } = useSchemaFormContext<TValues, TMeta, TSlot>();

  return (
    <Controller
      name={node.name}
      control={form.control}
      // Registered at once, so an untouched box still submits false.
      defaultValue={false as never}
      render={({ field }) => (
        <Checkbox
          {...anatomy}
          ref={field.ref}
          name={field.name}
          isSelected={Boolean(field.value)}
          onChange={field.onChange}
          onBlur={field.onBlur}
        >
          {label}
        </Checkbox>
      )}
    />
  );
}

function EnumField<TValues extends FieldValues, TMeta, TSlot extends FieldSlotDescriptor>({
  node,
  anatomy,
}: {
  node: Extract<FormFieldNode<TValues, TMeta, TSlot>, { kind: 'enum' }>;
  anatomy: FieldAnatomy;
}) {
  const { t } = useTranslation('integrations');
  const { form } = useSchemaFormContext<TValues, TMeta, TSlot>();
  const { field } = useController({ name: node.name, control: form.control });
  const clear = anatomy.isRequired
    ? ({ isClearable: false } as const)
    : ({
        isClearable: true,
        clearLabel: t('schemaForm.clear', { label: node.label, ...asText }),
      } as const);

  return (
    <Select
      {...anatomy}
      {...clear}
      ref={field.ref}
      name={field.name}
      placeholder={node.input?.isSecret ? node.input.placeholder : undefined}
      value={(field.value as string | undefined) ?? null}
      onChange={(value) => field.onChange(value ?? null)}
      onBlur={field.onBlur}
    >
      {node.options.map((option) => (
        <Option key={option.value} id={option.value}>
          {option.label}
        </Option>
      ))}
    </Select>
  );
}

function ObjectField<TValues extends FieldValues, TMeta, TSlot extends FieldSlotDescriptor>({
  node,
  anatomy,
}: {
  node: Extract<FormFieldNode<TValues, TMeta, TSlot>, { kind: 'object' }>;
  anatomy: FieldAnatomy;
}) {
  const { form } = useSchemaFormContext<TValues, TMeta, TSlot>();
  const { field } = useController({ name: node.name, control: form.control });

  if (!node.switchOnly && !node.switchable) {
    return (
      <Fieldset
        legend={anatomy.label}
        description={anatomy.description}
        error={anatomy.error}
        isRequired={anatomy.isRequired}
        isReadOnly={anatomy.isReadOnly}
        isDisabled={anatomy.isDisabled}
      >
        <NestedFields fields={node.children} />
      </Fieldset>
    );
  }

  const isOn = Boolean(field.value);

  return (
    <Stack gap={2}>
      <Checkbox
        ref={field.ref}
        name={field.name}
        description={anatomy.description}
        error={anatomy.error}
        isDisabled={anatomy.isDisabled}
        isReadOnly={anatomy.isReadOnly}
        isSelected={isOn}
        onChange={(isSelected) => field.onChange(isSelected ? {} : null)}
        onBlur={field.onBlur}
      >
        {anatomy.label}
      </Checkbox>
      {!node.switchOnly && isOn && <NestedFields fields={node.children} />}
    </Stack>
  );
}

function PrimitiveArrayField<
  TValues extends FieldValues,
  TMeta,
  TSlot extends FieldSlotDescriptor,
>({ node, anatomy }: { node: ArrayFormFieldNode<TValues, TMeta, TSlot>; anatomy: FieldAnatomy }) {
  const { t } = useTranslation('integrations');
  const { form } = useSchemaFormContext<TValues, TMeta, TSlot>();
  const { field } = useController({
    name: node.name,
    control: form.control,
    defaultValue: (node.required.input ? [] : undefined) as never,
  });
  const values = (Array.isArray(field.value) ? field.value : []).map(String);
  const toValue = (value: string) => (node.element.kind === 'number' ? Number(value) : value);
  const onChange = (next: string[]) => field.onChange(next.map(toValue));

  if (node.element.kind === 'enum') {
    return (
      <Select
        {...anatomy}
        ref={field.ref}
        name={field.name}
        selectionMode="multiple"
        removeLabel={(label) => t('schemaForm.array.removeValue', { value: label, ...asText })}
        value={values}
        onChange={onChange}
        onBlur={field.onBlur}
      >
        {node.element.options.map((option) => (
          <Option key={option.value} id={option.value}>
            {option.label}
          </Option>
        ))}
      </Select>
    );
  }

  return (
    <Combobox
      {...anatomy}
      ref={field.ref}
      name={field.name}
      selectionMode="multiple"
      value={values}
      onChange={onChange}
      onBlur={field.onBlur}
      removeLabel={(label) => t('schemaForm.array.removeValue', { value: label, ...asText })}
      onCreate={(text) => {
        const value = text.trim();
        const isNumber = node.element.kind !== 'number' || !isNaN(Number(value));
        // 1.0 and 01 store as 1: compare the stored value, or the list holds 1 twice.
        if (value && isNumber && !values.includes(String(toValue(value)))) {
          onChange([...values, value]);
        }
      }}
      createLabel={(text) => t('schemaForm.array.addValue', { value: text, ...asText })}
      emptyMessage={t('schemaForm.array.typeToAdd')}
    >
      {values.map((value) => (
        <Option key={value} id={value}>
          {value}
        </Option>
      ))}
    </Combobox>
  );
}

function ObjectArrayField<TValues extends FieldValues, TMeta, TSlot extends FieldSlotDescriptor>({
  node,
  anatomy,
}: {
  node: ArrayFormFieldNode<TValues, TMeta, TSlot>;
  anatomy: FieldAnatomy;
}) {
  const { form } = useSchemaFormContext<TValues, TMeta, TSlot>();
  const { t } = useTranslation('integrations');
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: node.name as ArrayPath<TValues>,
  });

  return (
    <Fieldset
      legend={anatomy.label}
      description={anatomy.description}
      error={anatomy.error}
      isRequired={anatomy.isRequired}
      isReadOnly={anatomy.isReadOnly}
      isDisabled={anatomy.isDisabled}
    >
      <Stack gap={4} align="start">
        {fields.map((field, index) => {
          const label = t('schemaForm.array.item', {
            label: anatomy.label,
            number: index + 1,
            ...asText,
          });
          const element = {
            ...withArrayIndex(node.element, node.name, index),
            label,
            description: undefined,
            required: { ...node.element.required, input: false },
          } as FormArrayElement<TValues, TMeta, TSlot>;
          return (
            <Stack key={field.id} direction="row" gap={2} align="start" isFilled>
              <Stack isFilled>
                <Card>
                  <Content>
                    {element.kind === 'object' ? (
                      <Fieldset legend={label}>
                        <FieldStack fields={element.children} />
                      </Fieldset>
                    ) : (
                      <SchemaField node={element} />
                    )}
                  </Content>
                </Card>
              </Stack>
              {!anatomy.isReadOnly && (
                <IconButton
                  label={t('schemaForm.array.removeItem', { label, ...asText })}
                  isDisabled={anatomy.isDisabled}
                  icon={TrashIcon}
                  onPress={() => remove(index)}
                />
              )}
            </Stack>
          );
        })}
        {!anatomy.isReadOnly && (
          <Button
            variant="ghost"
            size="sm"
            iconStart={PlusIcon}
            isDisabled={anatomy.isDisabled}
            onPress={() => append({} as FieldArray<TValues, ArrayPath<TValues>>)}
          >
            {t('schemaForm.array.addItem')}
          </Button>
        )}
      </Stack>
    </Fieldset>
  );
}

function UnionField<TValues extends FieldValues, TMeta, TSlot extends FieldSlotDescriptor>({
  node,
  anatomy,
}: {
  node: UnionFormFieldNode<TValues, TMeta, TSlot>;
  anatomy: FieldAnatomy;
}) {
  const { t } = useTranslation('integrations');
  const { form } = useSchemaFormContext<TValues, TMeta, TSlot>();
  const { field, fieldState } = useController({ name: node.name, control: form.control });
  const chosen = get(field.value, node.discriminator) as string | undefined;
  const selectedOption = node.options.find((option) => option.value === chosen);
  const discriminatorError = fieldState.error?.[node.discriminator as never] as
    | FieldError
    | undefined;
  const clear = anatomy.isRequired
    ? ({ isClearable: false } as const)
    : ({
        isClearable: true,
        clearLabel: t('schemaForm.clear', { label: node.label, ...asText }),
      } as const);

  return (
    <Stack gap={2}>
      <Select
        {...anatomy}
        {...clear}
        error={anatomy.error ?? (discriminatorError?.message || undefined)}
        ref={field.ref}
        name={field.name}
        placeholder={node.input?.isSecret ? node.input.placeholder : undefined}
        value={chosen ?? null}
        onChange={(value) => field.onChange(value ? { [node.discriminator]: value } : null)}
        onBlur={field.onBlur}
      >
        {node.options.map((option) => (
          <Option key={option.value} id={option.value}>
            {option.label}
          </Option>
        ))}
      </Select>
      {selectedOption && selectedOption.fields.length > 0 && (
        <NestedFields fields={selectedOption.fields} />
      )}
    </Stack>
  );
}

function CustomField<TValues extends FieldValues, TMeta, TSlot extends FieldSlotDescriptor>({
  node,
  anatomy,
}: {
  node: Extract<FormFieldNode<TValues, TMeta, TSlot>, { kind: 'custom' }>;
  anatomy: FieldAnatomy;
}) {
  const { form } = useSchemaFormContext<TValues, TMeta, TSlot>();

  switch (node.component) {
    case 'TimePeriod':
      return (
        // TimePeriod is several controls, so its name is a group's.
        <Fieldset
          legend={anatomy.label}
          description={anatomy.description}
          error={anatomy.error}
          isRequired={anatomy.isRequired}
          isReadOnly={anatomy.isReadOnly}
          isDisabled={anatomy.isDisabled}
        >
          <Controller
            name={node.name}
            control={form.control}
            render={({ field }) => (
              <TimePeriod
                value={field.value}
                isDisabled={anatomy.isDisabled}
                onChange={(timePeriod) => field.onChange(timePeriod)}
              />
            )}
          />
        </Fieldset>
      );
    default:
      throw new Error(`Unsupported custom component: ${node.component}`);
  }
}

function FieldStack<TValues extends FieldValues, TMeta, TSlot extends FieldSlotDescriptor>({
  fields,
}: {
  fields: readonly FormFieldNode<TValues, TMeta, TSlot>[];
}) {
  return (
    <Stack gap={4}>
      {fields.map((field) => (
        <SchemaField key={field.id} node={field} />
      ))}
    </Stack>
  );
}

/** The fields a choice or a group opens, on a card so they read as belonging to it. */
function NestedFields<TValues extends FieldValues, TMeta, TSlot extends FieldSlotDescriptor>({
  fields,
}: {
  fields: readonly FormFieldNode<TValues, TMeta, TSlot>[];
}) {
  return (
    <Card>
      <Content>
        <FieldStack fields={fields} />
      </Content>
    </Card>
  );
}

function useSchemaFormContext<
  TValues extends FieldValues,
  TMeta,
  TSlot extends FieldSlotDescriptor,
>() {
  const context = useContext(SchemaFormContext);

  if (!context) {
    throw new Error('Schema form fields must be rendered inside SchemaForm');
  }

  return context as unknown as SchemaFormContextValue<TValues, TMeta, TSlot>;
}

function toSchemaFormContextValue<
  TValues extends FieldValues,
  TMeta,
  TSlot extends FieldSlotDescriptor,
>(value: SchemaFormContextValue<TValues, TMeta, TSlot>) {
  return value as unknown as SchemaFormContextValue<FieldValues, unknown, FieldSlotDescriptor>;
}

function renderSlot<TValues extends FieldValues, TMeta, TSlot extends FieldSlotDescriptor>(
  slot: FieldSlot<TSlot> | undefined,
  node: FormFieldNode<TValues, TMeta, TSlot>,
  context: SchemaFormContextValue<TValues, TMeta, TSlot>,
): ReactNode {
  if (!slot) {
    return null;
  }

  const slotRegistry = context.slotRegistry as unknown as Partial<
    Record<string, FieldSlotRenderer<TValues, TMeta, TSlot>>
  >;
  const renderer = slotRegistry[slot.kind];
  if (!renderer) {
    throw new Error(`No renderer registered for field slot kind "${slot.kind}"`);
  }

  return renderer({
    slot: slot,
    node,
    form: context.form,
    isDisabled: context.isDisabled,
  });
}

function normalizeText(value: string) {
  const normValue = normalizeString(value);
  return typeof normValue === 'string' ? normValue.replace(/\\n/g, '\n') : normValue;
}

function normalizeNumber(value: string) {
  const normValue = normalizeString(value);
  if (typeof normValue !== 'string') {
    return normValue;
  }

  const numberValue = Number(normValue);
  return isNaN(numberValue) ? normValue : numberValue;
}

function normalizeDate(value: string) {
  const normValue = normalizeString(value);
  return typeof normValue === 'string' ? new Date(normValue) : normValue;
}

function normalizeString(value: string) {
  const normValue = value.trim();
  return normValue === '' ? undefined : normValue;
}

function formatScalar(value: unknown) {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

function formatDate(value: unknown) {
  return value instanceof Date && !isNaN(value.getTime())
    ? value.toISOString().slice(0, 10)
    : formatScalar(value);
}

/**
 * The error a field shows: its own, an array's own once it has rows (React
 * Hook Form nests it at `root`), or a primitive list's first bad entry.
 */
function findError(errors: FieldValues, name: string): FieldError | undefined {
  const error = get(errors, name) as FieldError | FieldError[] | undefined;
  if (!error) {
    return undefined;
  }
  if (Array.isArray(error)) {
    return error.find(Boolean);
  }
  return error.message ? error : ((error as { root?: FieldError }).root ?? error);
}

function isPrimitiveArrayElement<
  TValues extends FieldValues,
  TMeta,
  TSlot extends FieldSlotDescriptor,
>(node: FormArrayElement<TValues, TMeta, TSlot>) {
  return node.kind === 'text' || node.kind === 'number' || node.kind === 'enum';
}

function withArrayIndex<TValues extends FieldValues, TMeta, TSlot extends FieldSlotDescriptor>(
  node: FormArrayElement<TValues, TMeta, TSlot>,
  arrayName: FieldPath<TValues>,
  index: number,
): FormArrayElement<TValues, TMeta, TSlot> {
  const itemName = `${arrayName}.${index}`;
  return renameNode(node, node.name, itemName) as FormArrayElement<TValues, TMeta, TSlot>;
}

function renameNode<TValues extends FieldValues, TMeta, TSlot extends FieldSlotDescriptor>(
  node: FormFieldNode<TValues, TMeta, TSlot>,
  fromPrefix: string,
  toPrefix: string,
): FormFieldNode<TValues, TMeta, TSlot> {
  const name = renamePath(node.name, fromPrefix, toPrefix) as FieldPath<TValues>;
  const renamed = {
    ...node,
    id: renamePath(node.id, fromPrefix, toPrefix),
    name,
  };

  switch (renamed.kind) {
    case 'object':
      return {
        ...renamed,
        children: renamed.children.map((child) => renameNode(child, fromPrefix, toPrefix)),
      };
    case 'array':
      return {
        ...renamed,
        element: renameNode(renamed.element, fromPrefix, toPrefix) as FormArrayElement<
          TValues,
          TMeta,
          TSlot
        >,
      };
    case 'union':
      return {
        ...renamed,
        options: renamed.options.map((option) => ({
          ...option,
          fields: option.fields.map((field) => renameNode(field, fromPrefix, toPrefix)),
        })),
      };
    default:
      return renamed;
  }
}

function renamePath(path: string, fromPrefix: string, toPrefix: string) {
  return path === fromPrefix || path.startsWith(`${fromPrefix}.`)
    ? `${toPrefix}${path.slice(fromPrefix.length)}`
    : path;
}
